import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraOverlayDialog, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { RegistryEntityPicker } from '../../../components/SharedRecord/RegistryEntityPicker';
import { useTenantId } from '../../Delivery/_shared/hooks/useTenantId';
import { usePrograms } from '../../Delivery/Programs/hooks';
import { contractsClient } from '../../Delivery/Registries/Contracts/api';
import { useCanonicalOpportunities } from '../../Opportunities/hooks/useCanonicalOpportunities';
import { useUpsertSeatRound } from '../hooks/useLandscapeSeats';
const T = Testids.seatAdmin;
export function roundError(r) {
    if (![
        'prior',
        'current',
        'future'
    ].includes(r.status ?? '')) return 'Choose a status.';
    if (![
        'us',
        'competitor',
        'unknown',
        'none'
    ].includes(r.holderKind ?? '')) return 'Choose a holder kind.';
    if (r.holderKind !== 'competitor' && r.holderCompanyId) return 'Only competitor rounds may have a Company.';
    if (r.valueCents !== undefined && BigInt(r.valueCents) < BigInt(0)) return 'Value must be non-negative.';
    if (r.modsValueCents !== undefined && BigInt(r.modsValueCents) < BigInt(0)) return 'Modification value must be non-negative.';
    if (r.sourceBasis === 'program' && !r.programId) return 'Choose a Program for the source.';
    if (r.sourceBasis === 'registry_contract' && !r.registryContractId) return 'Choose a registry contract for the source.';
    if (r.sourceBasis === 'opportunity' && !r.opportunityId) return 'Choose an Opportunity for the source.';
    if (!Number.isInteger(r.roundIndex) || (r.roundIndex ?? 0) <= 0) return 'Round number must be positive.';
    if (r.status === 'future' && (r.holderKind !== 'none' || r.programId || r.registryContractId)) return 'Future rounds must have holder None and no program or registry contract.';
    if (r.status !== 'future' && r.holderKind === 'none') return 'None is only available for Future rounds.';
    if (r.holderKind === 'competitor' && !r.holderCompanyId) return 'Choose a Company for a competitor.';
    if (r.holderKind === 'us' && !r.programId) return 'Choose a Program for Us.';
    if (r.sourceBasis === 'manual' && !r.sourceNote?.trim()) return 'Add a source note for a manual round.';
    if (r.popStart && r.popEnd && r.popEnd < r.popStart) return 'PoP end must be on or after PoP start.';
    if (r.optionsEnd && r.popEnd && r.optionsEnd < r.popEnd) return 'Options end must be on or after PoP end.';
    return '';
}
export function SeatRoundForm({ seat, round, awardee, onClose }) {
    const [draft, setDraft] = useState(()=>({
            roundIndex: Math.max(0, ...seat.rounds.map((r)=>r.roundIndex)) + 1,
            status: 'prior',
            holderKind: awardee ? 'competitor' : 'unknown',
            sourceBasis: 'manual',
            ...round,
            vehicleId: round?.vehicleId || seat.vehicleId,
            ...awardee ? {
                holderKind: 'competitor'
            } : {}
        }));
    const [value, setValue] = useState(round?.valueCents === undefined ? '' : `${BigInt(round.valueCents) / BigInt(100)}.${(BigInt(round.valueCents) % BigInt(100)).toString().padStart(2, '0')}`);
    const [error, setError] = useState('');
    const save = useUpsertSeatRound();
    const tenant = useTenantId();
    const programs = usePrograms({
        companyId: tenant
    });
    const opportunities = useCanonicalOpportunities();
    const [contractSearch, setContractSearch] = useState('');
    const contracts = useQuery({
        queryKey: [
            'seat-admin-contracts',
            tenant,
            contractSearch
        ],
        queryFn: ()=>contractsClient.browseRegistryContracts({
                piid: contractSearch,
                limit: 30
            })
    });
    const set = (patch)=>{
        setError('');
        setDraft((d)=>({
                ...d,
                ...patch
            }));
    };
    const submit = async ()=>{
        const r = {
            ...draft,
            sourceBasis: draft.programId ? 'program' : draft.registryContractId ? 'registry_contract' : draft.opportunityId ? 'opportunity' : 'manual'
        };
        const invalid = roundError(r);
        if (invalid) {
            setError(invalid);
            return;
        }
        if (value && !/^\d+(\.\d{1,2})?$/.test(value)) {
            setError('Enter a non-negative USD value with at most two decimal places.');
            return;
        }
        const [dollars, cents = ''] = (value || '0').split('.');
        r.valueCents = BigInt(dollars) * BigInt(100) + BigInt(cents.padEnd(2, '0'));
        if (r.valueCents > BigInt('9223372036854775807')) {
            setError('USD value is too large.');
            return;
        }
        try {
            await save.mutateAsync({
                seatId: seat.id,
                round: r
            });
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };
    const input = (key, label, type = 'text')=>/*#__PURE__*/ _jsx(SpecraFormField, {
            label: label,
            htmlFor: `seat-round-${key}`,
            children: /*#__PURE__*/ _jsx(SpecraInput, {
                id: `seat-round-${key}`,
                type: type,
                value: draft[key] ?? '',
                onChange: (e)=>set({
                        [key]: e.target.value
                    }),
                "data-testid": T[`roundForm${key[0].toUpperCase()}${key.slice(1)}`]
            })
        });
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        title: round?.id ? 'Edit round' : 'Add round',
        onOpenChange: (v)=>{
            if (!v) onClose();
        },
        testId: T.roundFormRoot,
        footerProps: {
            cancelLabel: 'Cancel',
            onCancel: onClose,
            confirmLabel: 'Save round',
            onConfirm: submit,
            loading: save.isPending,
            confirmProps: {
                'data-testid': T.save
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    "data-testid": T.error,
                    children: error
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Status",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        "aria-label": "Status",
                        value: draft.status,
                        "data-testid": T.roundFormStatus,
                        onChange: (e)=>set(e.target.value === 'future' ? {
                                status: 'future',
                                holderKind: 'none',
                                holderCompanyId: '',
                                programId: '',
                                registryContractId: ''
                            } : {
                                status: e.target.value,
                                holderKind: draft.holderKind === 'none' ? 'unknown' : draft.holderKind
                            }),
                        children: [
                            'prior',
                            'current',
                            'future'
                        ].map((v)=>/*#__PURE__*/ _jsx("option", {
                                value: v,
                                children: v[0].toUpperCase() + v.slice(1)
                            }, v))
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Holder kind",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        "aria-label": "Holder kind",
                        value: draft.holderKind,
                        "data-testid": T.roundFormHolder,
                        onChange: (e)=>set({
                                holderKind: e.target.value,
                                holderCompanyId: '',
                                programId: ''
                            }),
                        children: (draft.status === 'future' ? [
                            'none'
                        ] : [
                            'us',
                            'competitor',
                            'unknown'
                        ]).map((v)=>/*#__PURE__*/ _jsx("option", {
                                value: v,
                                children: v === 'us' ? 'Us' : v[0].toUpperCase() + v.slice(1)
                            }, v))
                    })
                }),
                draft.holderKind === 'competitor' && /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                    kinds: [
                        'COMPANY'
                    ],
                    label: "Company",
                    value: draft.holderCompanyId,
                    selectedLabel: round?.holderLabel,
                    initialObservation: awardee,
                    "data-testid": T.roundFormCompany,
                    onPick: (o)=>set({
                            holderCompanyId: o.registryId
                        })
                }),
                draft.holderKind === 'us' && /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "Program",
                    value: draft.programId,
                    options: (programs.data?.items ?? []).map((p)=>({
                            value: p.id,
                            label: p.programNameFull || p.programNameShort
                        })),
                    onValueChange: (v)=>set({
                            programId: v ?? ''
                        }),
                    triggerProps: {
                        'data-testid': T.roundFormProgram
                    }
                }),
                /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                    kinds: [
                        'VEHICLE'
                    ],
                    createMode: "none",
                    label: "Vehicle",
                    value: draft.vehicleId,
                    selectedLabel: round?.vehicleLabel || seat.vehicleLabel,
                    onPick: (o)=>set({
                            vehicleId: o.registryId
                        }),
                    "data-testid": T.roundFormVehicle
                }),
                /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "Registry contract",
                    value: draft.registryContractId,
                    fallbackLabel: draft.registryContractId ? round?.label || draft.registryContractId : undefined,
                    disabled: draft.status === 'future',
                    searchValue: contractSearch,
                    onSearchValueChange: setContractSearch,
                    shouldFilter: false,
                    options: (contracts.data?.contracts ?? []).map((c)=>({
                            value: c.id,
                            label: c.identity?.piid || c.id
                        })),
                    onValueChange: (v)=>set({
                            registryContractId: v ?? ''
                        }),
                    emptyMessage: "No imported awards yet. Enter the round by hand and add a source note.",
                    triggerProps: {
                        'data-testid': T.roundFormContract
                    }
                }),
                contracts.isError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    children: String(contracts.error)
                }),
                /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "Opportunity",
                    value: draft.opportunityId,
                    options: (opportunities.data ?? []).map((o)=>({
                            value: o.id,
                            label: o.title
                        })),
                    onValueChange: (v)=>set({
                            opportunityId: v ?? ''
                        }),
                    triggerProps: {
                        'data-testid': T.roundFormOpportunity
                    }
                }),
                input('popStart', 'PoP start', 'date'),
                input('popEnd', 'PoP end', 'date'),
                input('optionsEnd', 'Options end', 'date'),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Value (USD)",
                    htmlFor: "seat-round-value",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "seat-round-value",
                        inputMode: "decimal",
                        value: value,
                        onChange: (e)=>setValue(e.target.value),
                        "data-testid": T.roundFormValue
                    })
                }),
                input('label', 'Label'),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Source note",
                    htmlFor: "seat-round-sourceNote",
                    hint: "Required when no program, registry contract or opportunity is linked.",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        id: "seat-round-sourceNote",
                        value: draft.sourceNote ?? '',
                        onChange: (e)=>set({
                                sourceNote: e.target.value
                            }),
                        "data-testid": T.roundFormSourceNote
                    })
                })
            ]
        })
    });
}
